import { Drawer, Loading } from '@/components';
import { useProject } from '@/features/project/_hooks/project';
import {
  useFundBusinesses,
  useUpdateFundBusiness,
} from '@/features/project/projects/detail/fund-bussiness/_hooks/fund-business';
import type { FundBusinessFormValues } from '@/features/project/projects/detail/fund-bussiness/_model/fund-business';
import { useCurrentFiscalYear } from '@/features/search/_hooks/get-current-fiscal-year';
import { useId } from '@/hooks';
import { useMessage } from '@/providers';
import { logger } from '@/utils';
import { type FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import { useProjectDetailDrawerState } from '../../../_components/ProjectDrawerStateProvider';
import { getInitialValueOfCalculatedInvestmentBalance } from '../../_utils/form-control';
import { ActualResultsForm } from './forms/ActualResultsForm';
import { OthersForm } from './forms/OthersForm';
import { SubsidyForm } from './forms/SubsidyForm';

export const UpdateFundBusinessFormDrawer: FC = () => {
  const { drawerState, setDrawerState } = useProjectDetailDrawerState();
  const { t } = useTranslation();
  const { openMessage } = useMessage();
  const { id: projectId } = useId();
  const project = useProject(projectId);
  const { mutateAsync: updateFundBusiness } = useUpdateFundBusiness(projectId);

  const currentFiscalYear = useCurrentFiscalYear();

  const fiscalYear =
    project.data?.fiscalYear ??
    currentFiscalYear.data ??
    currentFiscalYear.basicFiscalYear;

  const isDrawerOpen =
    drawerState.open &&
    drawerState.type === 'fund-business' &&
    project.data?.fundAdditionalAttributes?.fundSheetType === 'standard' &&
    drawerState.isEditing;

  const fundBusiness = useFundBusinesses({
    projectId,
    select: (data) => {
      if (isDrawerOpen && drawerState.fundBusinessId) {
        return data.find((v) => v.id === drawerState.fundBusinessId);
      }

      return undefined;
    },
  });

  const closeDrawer = useCallback(() => {
    setDrawerState({ open: false });
  }, [setDrawerState]);

  const onSubmit = useCallback(
    async (values: FundBusinessFormValues) => {
      invariant(fundBusiness.data?.id, t('unreachable-code'));

      try {
        await updateFundBusiness({
          fundBusinessId: fundBusiness.data.id,
          values: {
            title: values.title,
            businessForm: values.businessForm,
            performances: values.performances,
          },
        });
        openMessage('success', t('message-update-completed'));
        closeDrawer();
      } catch {
        openMessage('error', t('message-update-failed'));
      }
    },
    [closeDrawer, fundBusiness.data?.id, openMessage, t, updateFundBusiness],
  );

  const renderItem = useMemo(() => {
    if (fundBusiness.isPending) {
      return <Loading />;
    }

    const initialValues = getInitialValueOfCalculatedInvestmentBalance({
      fundBusiness: fundBusiness.data,
      fiscalYear,
    });

    switch (fundBusiness.data?.businessForm) {
      /** 補助等に関する交付決定実績 */
      case 'subsidy':
        return (
          <SubsidyForm
            onSubmit={onSubmit}
            initialValues={initialValues}
            fiscalYear={fiscalYear}
          />
        );
      /** 出資実績 */
      case 'investment':
      /** 債務保証実績 */
      case 'debt-guarantee':
      /** 貸付実績 */
      case 'loan':
        return (
          <ActualResultsForm
            onSubmit={onSubmit}
            initialValues={initialValues}
            fiscalYear={fiscalYear}
          />
        );
      /** その他 */
      case 'others':
        return (
          <OthersForm
            onSubmit={onSubmit}
            initialValues={initialValues}
            fiscalYear={fiscalYear}
          />
        );
      default: {
        // 出資状況表側のbusinessFormも定義されているため
        const check:
          | never
          | 'interest-subsidy'
          | 'compensation'
          | 'investigation'
          | undefined = fundBusiness.data?.businessForm;
        logger.error('unreachable businessForm', { check });
        return null;
      }
    }
  }, [fundBusiness, fiscalYear, onSubmit]);

  return (
    <Drawer
      size="large"
      destroyOnHidden
      open={isDrawerOpen}
      onClose={closeDrawer}
    >
      {renderItem}
    </Drawer>
  );
};
